import React, { useState, useEffect } from 'react';
import { motion, MotionConfig } from 'motion/react';
import { DownloadSimple, CaretDown, CaretLeft, CaretRight, WarningCircle, Check, X, ArrowRight, MagnifyingGlass } from '@phosphor-icons/react';
import authedFetch from '../lib/authedFetch';

// Entrada dos blocos: só opacidade, 140ms, curva do DS, sem deslocamento nem cascata
// (§5: motion orienta, nunca decora; o MotionConfig respeita prefers-reduced-motion).
const cardAnim = () => ({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.14, ease: [0.2, 0, 0, 1] },
});

// Classes do kit do CareOS (src/styles/care-ui.css)
const CARD = 'ui-card';
const LABEL = 'ui-rotulo';
const SKELETON = 'skel';
const BAR_ANIM = 'transition-[width] duration-slow ease-care motion-reduce:transition-none';

// Percentual com uma casa, sempre no formato pt-BR (12,5)
const fmtPct1 = (v) => v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

// Barra de proporção qualificados x desqualificados (um bloco por origem). Ícone + número + palavra: cor nunca sozinha.
const BarraOrigem = ({ qualificados, desqualificados, percQualif, percDesqual }) => (
  <>
    <div className='flex h-6 rounded-sm overflow-hidden bg-[var(--care-bg-secondary)]'>
      {percQualif > 0 && (
        <div
          className={`bg-[var(--care-positive-text)] ${BAR_ANIM} flex items-center justify-center`}
          style={{ width: `${percQualif}%` }}
          title={`${qualificados} qualificados (${fmtPct1(percQualif)}%)`}
        >
          {percQualif > 15 && <span className='care-num text-[11px] font-semibold text-[color:var(--care-text-inverse)]'>{percQualif.toFixed(0)}%</span>}
        </div>
      )}
      {percDesqual > 0 && (
        <div
          className={`bg-[var(--care-negative-text)] ${BAR_ANIM} flex items-center justify-center`}
          style={{ width: `${percDesqual}%` }}
          title={`${desqualificados} desqualificados (${fmtPct1(percDesqual)}%)`}
        >
          {percDesqual > 15 && <span className='care-num text-[11px] font-semibold text-[color:var(--care-text-inverse)]'>{percDesqual.toFixed(0)}%</span>}
        </div>
      )}
    </div>
    <div className='flex flex-wrap items-center justify-between gap-2 text-[13px]'>
      <span className='text-[color:var(--care-positive-text)] font-semibold flex items-center gap-1'>
        <Check size={16} weight='bold' aria-hidden='true' />
        <span className='care-num'>{qualificados}</span> qualificados
        <span className='care-num ui-meta font-normal'>({percQualif.toFixed(0)}%)</span>
      </span>
      <span className='text-[color:var(--care-negative-text)] font-semibold flex items-center gap-1'>
        <X size={16} weight='bold' aria-hidden='true' />
        <span className='care-num'>{desqualificados}</span> desqualificados
        <span className='care-num ui-meta font-normal'>({percDesqual.toFixed(0)}%)</span>
      </span>
    </div>
  </>
);

const LeadsAplicacao = () => {
  const [data, setData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [atualizadoEm, setAtualizadoEm] = useState(null);      // timestamp do último fetch (não de cada render)
  const [updating, setUpdating] = useState(new Set());          // "responseId:campo" com PUT em andamento
  const [erros, setErros] = useState(new Set());                // "responseId:campo" cujo último PUT falhou
  const [viewMode, setViewMode] = useState('semanal');
  const [expandedLeads, setExpandedLeads] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [baixando, setBaixando] = useState(false);
  const [erroDownload, setErroDownload] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    carregarDados();
  }, [selectedYear]);

  const carregarDados = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authedFetch(`${API_URL}/api/typeform/leads/aplicacao?year=${selectedYear}`);
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      setData(await res.json());
      setAtualizadoEm(new Date());
    } catch (err) {
      console.error('Erro:', err);
      setError(err.message || 'Falha ao carregar');
    } finally {
      setLoading(false);
    }
  };

  // Checkboxes com update otimista: UI responde na hora, desfaz o campo se a API
  // falhar e desabilita o checkbox durante o PUT (sem recarregar a página inteira).
  const marcarCampo = async (responseId, campo, valor, url) => {
    const chave = `${responseId}:${campo}`;
    setErros((prev) => { if (!prev.has(chave)) return prev; const n = new Set(prev); n.delete(chave); return n; });
    setUpdating((prev) => new Set(prev).add(chave));
    setData((prev) => prev && ({
      ...prev,
      leads: (prev.leads || []).map((l) => (l.response_id === responseId ? { ...l, [campo]: valor } : l)),
    }));
    try {
      const res = await authedFetch(url, { method: 'PUT' });
      if (!res.ok) throw new Error(`Erro ${res.status}`);
    } catch (err) {
      console.error('Erro:', err);
      // rollback só do campo alterado
      setData((prev) => prev && ({
        ...prev,
        leads: (prev.leads || []).map((l) => (l.response_id === responseId ? { ...l, [campo]: !valor } : l)),
      }));
      setErros((prev) => new Set(prev).add(chave));
    } finally {
      setUpdating((prev) => { const n = new Set(prev); n.delete(chave); return n; });
    }
  };

  const marcarContato = (responseId, contatoRealizado) =>
    marcarCampo(responseId, 'contato_realizado', contatoRealizado,
      `${API_URL}/api/typeform/leads/aplicacao/${responseId}/contato?contato_realizado=${contatoRealizado}`);

  const marcarReuniao = (responseId, reuniaoRealizada) =>
    marcarCampo(responseId, 'reuniao_realizada', reuniaoRealizada,
      `${API_URL}/api/typeform/leads/aplicacao/${responseId}/reuniao?reuniao_realizada=${reuniaoRealizada}`);

  const toggleLead = (responseId) => {
    const newExpanded = new Set(expandedLeads);
    if (newExpanded.has(responseId)) {
      newExpanded.delete(responseId);
    } else {
      newExpanded.add(responseId);
    }
    setExpandedLeads(newExpanded);
  };

  const downloadExcel = async () => {
    if (baixando) return;
    setBaixando(true);
    setErroDownload(null);
    try {
      const res = await authedFetch(`${API_URL}/api/typeform/leads/aplicacao/download-qualificados`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `leads_aplicacao_${new Date().toISOString().split('T')[0]}.xlsx`;
        a.click();
        // revoga depois do clique (o atraso evita cortar o download no Safari)
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } else {
        setErroDownload('Não foi possível gerar o Excel.');
      }
    } catch (err) {
      console.error('Erro:', err);
      setErroDownload('Não foi possível gerar o Excel.');
    } finally {
      setBaixando(false);
    }
  };

  const leadsQualificados = (data?.leads?.filter(l => l.qualificado === true) || []).filter(l => {
    if (!searchTerm) return true;
    const nome = (l.nome || `${l.respostas?.oM9hIYlRwMQ9 || ''} ${l.respostas?.DPIy4hvUaGat || ''}`).toLowerCase();
    return nome.includes(searchTerm.toLowerCase());
  });
  const mediaAnual = data?.total_leads && data?.weekly_metrics?.length > 0
    ? (data.total_leads / data.weekly_metrics.length).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : '0,0';
  // Barra do detalhamento normalizada pelo maior valor real da série (antes: /20 fixo, estourava 100%)
  const maxSemanal = Math.max(1, ...((data?.weekly_metrics || []).map((w) => w.count)));

  // Gráfico de evolução: 12 semanas mais recentes ou 12 meses mais recentes (monthly_metrics vem do backend em ordem crescente)
  const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  const serieEvolucao = viewMode === 'mensal'
    ? (data?.monthly_metrics || []).slice(-12).map((m) => ({ chave: m.month, count: m.leads_total, rotulo: MESES[Number(m.month.split('-')[1]) - 1] }))
    : (data?.weekly_metrics || []).slice(0, 12).reverse().map((w) => ({ chave: w.week, count: w.count, rotulo: w.week.split('-W')[1] }));
  const maxEvolucao = Math.max(1, ...serieEvolucao.map((p) => p.count));
  const pctQualificados = data?.total_leads ? (data.qualificados / data.total_leads) * 100 : 0;

  // Leads por origem (mesmos filtros de antes, calculados uma vez para a pílula e para a barra)
  const organicos = data?.leads?.filter(l => l.origem === 'Orgânico') || [];
  const anuncios = data?.leads?.filter(l => l.origem === 'Anúncio') || [];
  const resumoOrigem = (lista) => {
    const qualificados = lista.filter(l => l.qualificado).length;
    const desqualificados = lista.length - qualificados;
    const percQualif = lista.length > 0 ? (qualificados / lista.length * 100) : 0;
    const percDesqual = lista.length > 0 ? (desqualificados / lista.length * 100) : 0;
    return { qualificados, desqualificados, percQualif, percDesqual };
  };

  // Checkbox de Contato/Reunião com estado de erro (mensagem + aria-describedby)
  const renderMarca = (lead, campo, rotulo, onMarcar) => {
    const chave = `${lead.response_id}:${campo}`;
    const ocupado = updating.has(chave);
    const comErro = erros.has(chave);
    return (
      <div className='flex flex-col justify-center'>
        <label
          className='flex items-center gap-2 min-h-[40px] cursor-pointer'
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type='checkbox'
            checked={lead[campo] || false}
            disabled={ocupado}
            aria-disabled={ocupado}
            aria-describedby={comErro ? `erro-${chave}` : undefined}
            onChange={(e) => {
              e.stopPropagation();
              onMarcar(lead.response_id, e.target.checked);
            }}
            className='w-5 h-5 rounded-sm cursor-pointer disabled:opacity-[.45] disabled:cursor-not-allowed'
            style={{ accentColor: 'var(--care-accent-solid)' }}
            onClick={(e) => e.stopPropagation()}
          />
          <span className={LABEL}>{rotulo}</span>
        </label>
        {comErro && (
          <span id={`erro-${chave}`} className='ui-meta' data-tom='negativo'>Não salvou. Tente de novo.</span>
        )}
      </div>
    );
  };

  return (
    <MotionConfig reducedMotion='user'>
    <div className='min-h-screen'>
      <div className='ui-pagina'>

        {/* Botão Voltar */}
        <button
          type='button'
          className='ui-botao -ml-2 mb-4'
          data-variante='fantasma'
          onClick={() => window.history.back()}
        >
          <CaretLeft size={16} weight='bold' aria-hidden='true' />
          Voltar
        </button>

        {/* Cabeçalho da tela */}
        <header className='ui-tela'>
          <div className='ui-tela__texto'>
            <span className='ui-eyebrow' data-tom='acento'>Care MFO</span>
            <h1 className='ui-tela__titulo'>Leads Aplicação</h1>
          </div>
          <div className='ui-tela__acoes'>
            <label className='ui-trilho' data-gap='3'>
              <span className='ui-rotulo'>Ano</span>
              <select
                className='ui-campo'
                value={selectedYear || ''}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              >
                {data?.available_years?.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </label>
          </div>
        </header>

        {/* Loading: skeleton na forma final do conteúdo */}
        {loading && (
          <div role='status' aria-busy='true' aria-live='polite'>
            <span className='sr-only'>Carregando leads...</span>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8' aria-hidden='true'>
              {[0, 1].map((i) => (
                <div key={i} className={CARD}>
                  <div className={`${SKELETON} h-5 w-32`} />
                  <div className={`${SKELETON} h-6 w-full`} />
                  <div className={`${SKELETON} h-4 w-1/2`} />
                </div>
              ))}
            </div>
            <div className='grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8' aria-hidden='true'>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={CARD} data-densidade='compacta'>
                  <div className={`${SKELETON} h-4 w-24`} />
                  <div className={`${SKELETON} h-9 w-16`} />
                </div>
              ))}
            </div>
            <div className={`${CARD} mb-6`} aria-hidden='true'>
              <div className={`${SKELETON} h-5 w-40`} />
              <div className={`${SKELETON} h-40 w-full`} />
            </div>
            <div className={`${CARD} mb-6`} aria-hidden='true'>
              <div className={`${SKELETON} h-5 w-48`} />
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className={`${SKELETON} h-6 w-full`} />
              ))}
            </div>
            <div className={CARD} aria-hidden='true'>
              <div className={`${SKELETON} h-5 w-56`} />
              <div className='ui-linhas ui-carregando'>
                {[0, 1, 2].map((i) => (
                  <div key={i} className='ui-linha' data-i={i}>
                    <span className='skel ui-carregando__nome' />
                    <span className='skel ui-carregando__meta' />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Erro: frase à vista, detalhe técnico recolhido */}
        {!loading && error && (
          <div role='alert' className='ui-card ui-pilha mb-8' data-gap='4'>
            <p className='ui-aviso' data-tom='negativo'>
              <WarningCircle size={16} weight='bold' aria-hidden='true' />
              Não foi possível carregar os dados.
            </p>
            <details className='ui-detalhe'>
              <summary className='ui-detalhe__resumo'>
                <span className='ui-detalhe__chevron'><CaretRight size={12} weight='bold' aria-hidden='true' /></span>
                Ver detalhe
              </summary>
              <div className='ui-detalhe__corpo'>{error}</div>
            </details>
            <div>
              <button type='button' className='ui-botao' data-variante='fio' onClick={carregarDados}>
                Tentar de novo
              </button>
            </div>
          </div>
        )}

        {/* Barras de Proporção por Origem */}
        {!loading && !error && data && (
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8'>
            {/* Barra Orgânico */}
            <motion.div {...cardAnim(0)} className={CARD}>
              <div className='ui-cabecalho items-center'>
                <h3 className='ui-titulo' data-tamanho='sm'>Orgânico</h3>
                <span className='ui-cabecalho__acoes'>
                  <span className='ui-pilula' data-tom='forte'>{organicos.length} leads</span>
                </span>
              </div>
              <BarraOrigem {...resumoOrigem(organicos)} />
            </motion.div>

            {/* Barra Anúncio */}
            <motion.div {...cardAnim(1)} className={CARD}>
              <div className='ui-cabecalho items-center'>
                <h3 className='ui-titulo' data-tamanho='sm'>Anúncio</h3>
                <span className='ui-cabecalho__acoes'>
                  <span className='ui-pilula' data-tom='forte'>{anuncios.length} leads</span>
                </span>
              </div>
              <BarraOrigem {...resumoOrigem(anuncios)} />
            </motion.div>
          </div>
        )}

        {/* Cards de Estatísticas */}
        {!loading && !error && data && (
          <>
            <motion.div {...cardAnim(2)} className='grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8'>
              <div className={CARD} data-densidade='compacta'>
                <div className='ui-kpi' data-tamanho='lg'>
                  <span className='ui-kpi__rotulo'>Total no ano</span>
                  <span className='ui-kpi__valor'>{data.total_leads}</span>
                </div>
              </div>

              <div className={CARD} data-densidade='compacta'>
                <div className='ui-kpi' data-tamanho='lg'>
                  <span className='ui-kpi__rotulo'>Semanas com leads</span>
                  <span className='ui-kpi__valor'>{data.weekly_metrics?.length || 0}</span>
                </div>
              </div>

              <div className={CARD} data-densidade='compacta'>
                <div className='ui-kpi' data-tamanho='lg'>
                  <span className='ui-kpi__rotulo'>Média anual</span>
                  <span className='ui-kpi__valor'>{mediaAnual}<span className='ui-kpi__de'>por semana</span></span>
                </div>
              </div>

              <div className={CARD} data-densidade='compacta'>
                <div className='ui-kpi' data-tamanho='lg' data-tom='positivo'>
                  <span className='ui-kpi__rotulo'>Qualificados</span>
                  <span className='ui-kpi__valor'>{data.qualificados}<span className='ui-kpi__de'>{fmtPct1(pctQualificados)}% do total</span></span>
                </div>
              </div>
            </motion.div>

            {/* Evolução Semanal */}
            <motion.div {...cardAnim(3)} className={`${CARD} mb-6`}>
              <div className='ui-cabecalho items-center'>
                <h3 className='ui-titulo' data-tamanho='lg'>{viewMode === 'mensal' ? 'Leads por mês' : 'Leads por semana'}</h3>
                <div className='ui-cabecalho__acoes'>
                  <div className='ui-segmentado' role='group' aria-label='Periodicidade'>
                    {[['semanal', 'Semanal'], ['mensal', 'Mensal']].map(([modo, rotulo]) => (
                      <button
                        key={modo}
                        type='button'
                        className='ui-segmentado__opcao'
                        aria-pressed={viewMode === modo}
                        onClick={() => setViewMode(modo)}
                      >
                        {rotulo}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div role='img' aria-label={viewMode === 'mensal' ? 'Leads nos 12 meses mais recentes com leads' : 'Leads nas 12 semanas mais recentes com leads'}>
                {/* Área das barras com altura definida: a coluna ocupa os 160px e a barra é
                    uma fração do espaço que sobra abaixo do rótulo (h-5 + mb-1 = 1.5rem). */}
                <div className='h-40 flex items-end gap-2 border-b border-[color:var(--care-chart-axis)]'>
                  {serieEvolucao.map((week) => {
                    const fracao = week.count / maxEvolucao;
                    return (
                      <div key={week.chave} className='flex-1 h-full flex flex-col justify-end items-center min-w-0'>
                        <div className='care-num text-center h-5 leading-5 mb-1 text-[13px] text-[color:var(--care-chart-label)]'>{week.count}</div>
                        <div
                          className='w-full bg-[var(--care-series-1)] rounded-t-sm'
                          style={{ height: `calc((100% - 1.5rem) * ${fracao})`, minHeight: week.count > 0 ? '8px' : '2px' }}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className='flex gap-2 mt-2' aria-hidden='true'>
                  {serieEvolucao.map((week) => (
                    <div key={week.chave} className='flex-1 min-w-0 care-num text-center text-[12px] text-[color:var(--care-chart-label)]'>
                      {week.rotulo}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Detalhamento Semanal */}
            <motion.div {...cardAnim(4)} className={`${CARD} mb-6`}>
              <h3 className='ui-titulo' data-tamanho='lg'>Detalhamento semanal ({selectedYear})</h3>
              <div className='space-y-2'>
                {data.weekly_metrics?.map((week) => (
                  <div key={week.week} className='flex items-center gap-3'>
                    <span className='care-num text-left text-[13px] text-[color:var(--care-chart-label)] w-24 shrink-0'>{week.week}</span>
                    <div className='flex-1 bg-[var(--care-bg-secondary)] rounded-sm h-6 overflow-hidden'>
                      <div
                        className={`bg-[var(--care-series-1)] h-full ${BAR_ANIM}`}
                        style={{ width: `${week.count > 0 ? Math.min(100, (week.count / maxSemanal) * 100) : 5}%` }}
                      />
                    </div>
                    <span className='care-num text-[13px] font-semibold text-[color:var(--care-text-primary)] w-8 shrink-0'>{week.count}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tabela de Leads Qualificados para Middle Office */}
            <motion.div {...cardAnim(5)} className={CARD}>
              <div className='ui-cabecalho items-center'>
                <h3 className='ui-titulo' data-tamanho='lg'>Leads qualificados para o Middle Office</h3>
                <div className='ui-cabecalho__acoes'>
                  <button
                    type='button'
                    className='ui-botao'
                    data-variante='solido'
                    aria-disabled={baixando || undefined}
                    aria-describedby={erroDownload ? 'erro-download' : undefined}
                    onClick={downloadExcel}
                  >
                    <DownloadSimple size={16} weight='bold' aria-hidden='true' />
                    {baixando ? 'Gerando Excel…' : 'Baixar Excel'}
                  </button>
                </div>
              </div>
              {erroDownload && (
                <p className='ui-aviso' data-tom='negativo' id='erro-download'>
                  <WarningCircle size={16} weight='bold' aria-hidden='true' />
                  {erroDownload}
                </p>
              )}

              <div className='relative text-[color:var(--care-text-tertiary)]'>
                <MagnifyingGlass
                  size={16}
                  weight='bold'
                  aria-hidden='true'
                  className='absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none'
                />
                <input
                  type='search'
                  className='ui-campo w-full pl-10'
                  aria-label='Buscar lead por nome'
                  placeholder='Buscar lead por nome…'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className='ui-linhas'>
                {leadsQualificados.length === 0 ? (
                  searchTerm ? (
                    <div className='ui-vazio-bloco' data-area>
                      <p className='ui-vazio'>Nenhum lead com “{searchTerm}”.</p>
                      <button type='button' className='ui-vazio__caminho' onClick={() => setSearchTerm('')}>
                        Limpar busca
                        <ArrowRight size={16} weight='bold' aria-hidden='true' />
                      </button>
                    </div>
                  ) : (
                    <div className='ui-vazio-bloco' data-area>
                      <p className='ui-vazio'>Nenhum lead qualificado ainda.</p>
                    </div>
                  )
                ) : (
                  leadsQualificados.map((lead) => {
                    const isExpanded = expandedLeads.has(lead.response_id);
                    const painelId = `lead-${lead.response_id}`;
                    return (
                      <div key={lead.response_id} className='[&+&]:border-t border-border'>
                        <div
                          className='ui-linha p-4'
                          data-interativa
                          onClick={() => toggleLead(lead.response_id)}
                        >
                          <div className='flex-1 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4'>
                            <div>
                              <p className={LABEL}>Data</p>
                              <p className='care-num text-left text-[15px] font-medium text-[color:var(--care-text-primary)]'>
                                {new Date(lead.submitted_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                              </p>
                            </div>
                            <div>
                              <p className={LABEL}>Nome</p>
                              <p className='text-[15px] font-medium text-[color:var(--care-text-primary)]'>
                                {lead.nome || `${lead.respostas?.oM9hIYlRwMQ9 || ''} ${lead.respostas?.DPIy4hvUaGat || ''}`.trim() || '-'}
                              </p>
                            </div>
                            <div>
                              <p className={LABEL}>CPF</p>
                              <p className='text-[15px] text-[color:var(--care-text-primary)]' style={{ fontFamily: 'var(--care-font-mono)' }}>{lead.cpf || lead.respostas?.tMcn0KRDKsYD || '-'}</p>
                            </div>
                            <div>
                              <p className={LABEL}>Telefone</p>
                              <p className='care-num text-left text-[15px] text-[color:var(--care-text-primary)]'>{lead.telefone || lead.respostas?.['402wPlNnH4Vc'] || '-'}</p>
                            </div>
                            <div>
                              <p className={LABEL}>Status</p>
                              <span className='ui-pilula' data-tom={lead.qualificado ? 'positivo' : 'negativo'}>
                                {lead.qualificado
                                  ? <Check size={12} weight='bold' aria-hidden='true' />
                                  : <X size={12} weight='bold' aria-hidden='true' />}
                                {lead.qualificado ? 'Qualificado' : 'Não qualificado'}
                              </span>
                            </div>
                            {renderMarca(lead, 'contato_realizado', 'Contato', marcarContato)}
                            {renderMarca(lead, 'reuniao_realizada', 'Reunião', marcarReuniao)}
                          </div>
                          <button
                            type='button'
                            className='ui-botao'
                            data-variante='fantasma'
                            data-so-icone
                            aria-expanded={isExpanded}
                            aria-controls={painelId}
                            aria-label={isExpanded ? 'Recolher respostas' : 'Ver respostas'}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleLead(lead.response_id);
                            }}
                          >
                            <CaretDown
                              size={20}
                              aria-hidden='true'
                              className={`transition-transform duration-fast ease-care motion-reduce:transition-none ${isExpanded ? 'rotate-180' : ''}`}
                            />
                          </button>
                        </div>

                        {isExpanded && (
                          <div id={painelId} className='p-4 border-t border-border'>
                            <h4 className='ui-rotulo mb-4' data-tom='forte'>Respostas completas</h4>
                            <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
                              <div>
                                <p className={`${LABEL} mb-1`}>E-mail</p>
                                <p className='text-[15px] text-[color:var(--care-text-primary)] break-words'>{lead.email || lead.respostas?.RFVP931C33mV || '-'}</p>
                              </div>
                              <div>
                                <p className={`${LABEL} mb-1`}>Telefone</p>
                                <p className='care-num text-left text-[15px] text-[color:var(--care-text-primary)]'>{lead.telefone || lead.respostas?.['402wPlNnH4Vc'] || '-'}</p>
                              </div>
                              <div>
                                <p className={`${LABEL} mb-1`}>CPF</p>
                                <p className='text-[15px] text-[color:var(--care-text-primary)]' style={{ fontFamily: 'var(--care-font-mono)' }}>{lead.cpf || lead.respostas?.tMcn0KRDKsYD || '-'}</p>
                              </div>
                              <div>
                                <p className={`${LABEL} mb-1`}>Renda mensal</p>
                                <p className='care-num text-left text-[15px] text-[color:var(--care-text-primary)]'>{lead.respostas?.Fk6qaklsLGZ0 || lead.renda_mensal || '-'}</p>
                              </div>
                              <div>
                                <p className={`${LABEL} mb-1`}>Patrimônio</p>
                                <p className='care-num text-left text-[15px] text-[color:var(--care-text-primary)]'>{lead.respostas?.qXPBLW9bEORW || lead.patrimonio || '-'}</p>
                              </div>
                              <div>
                                <p className={`${LABEL} mb-1`}>Capacidade de investimento</p>
                                <p className='care-num text-left text-[15px] text-[color:var(--care-text-primary)]'>{lead.respostas?.hHwv1DkikHRZ || lead.capacidade_poupanca || '-'}</p>
                              </div>
                              <div className='sm:col-span-3'>
                                <p className={`${LABEL} mb-1`}>Critério de qualificação</p>
                                <span className='ui-pilula' data-tom='forte'>
                                  {lead.criterio_qualificacao || 'Automático'}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </motion.div>

            <p className='ui-meta text-center mt-6'>
              Atualizado em <span className='care-num'>{atualizadoEm ? atualizadoEm.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—'}</span>
            </p>
          </>
        )}
      </div>
    </div>
    </MotionConfig>
  );
};

export default LeadsAplicacao;
